import { mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * The butterfly's other wing — one wing is painted, and the child paints its reflection.
 *
 * Symmetry, which a preschool curriculum teaches with a fold and a blot, and which this bundle
 * could not produce: odd-one-out asks a child to RECOGNISE a reflection among four cards, and
 * recognising one is a long way from making one.
 *
 * **This replaced a version that was the copying game wearing a different name**, and the reasons
 * are worth keeping because they are the design.
 *
 * *It looked identical to `vs-build-copy`.* Same renderer, same stars, two stacked panels; only the
 * right answer differed. A child could not tell which game they had opened and neither could a
 * teacher watching over their shoulder — and a game whose entire distinguishing feature is
 * invisible is a game that will be played as the other one.
 *
 * *The fold was top-to-bottom*, which is the axis that matters least. The reflection worth
 * practising in the year before reading is left-right — it is the b/d distinction — and the old
 * file admitted this and chose the other axis anyway, because two three-column panels side by side
 * put every cell under the size a five-year-old's finger lands on. That was a real constraint
 * answered by giving up the thing the game was for. The answer here is fewer columns, not the wrong
 * axis: two per wing at about 76 px, which is exactly the floor.
 *
 * *And there was no line.* "Fold along the line" is the whole idea and nothing on screen was a
 * line. Here the line is the butterfly's body, which is a line a child does not have to be told
 * about.
 *
 * **Colour is the second axis, and it is what makes the top of the ladder a different question.**
 * With one colour the child mirrors positions. With three, a spot has to arrive in the right place
 * AND the right colour, and the two can be got wrong independently — which is a genuinely harder
 * task rather than the same one with more spots.
 */
/** Colours a wing may use. The renderer holds the hues; a spot holds an index or `MIRROR_EMPTY`. */
export const MIRROR_COLORS = 3;
/** A spot with nothing on it. */
export const MIRROR_EMPTY = -1;
export const MIRROR_MIN_DIFFICULTY = 1;
export const MIRROR_MAX_DIFFICULTY = 8;
/**
 * Eight levels: more spots, then colour, then both.
 *
 * **Level four steps the spot count back when colour arrives**, and that is deliberate rather than
 * an ordering mistake. It is the argument sudoku makes when it swaps shapes for digits and the key
 * game makes when its key starts being counted: a child meeting a second thing to get right is not
 * where they were with the first, and pretending otherwise records a colour problem as a collapse
 * in a skill they still have.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        // Two spots and one colour: the whole idea of a reflection with nothing else to carry.
        case 1:
            return { spots: 2, colours: 1 };
        case 2:
            return { spots: 3, colours: 1 };
        case 3:
            return { spots: 4, colours: 1 };
        // Colour arrives.
        case 4:
            return { spots: 3, colours: 2 };
        case 5:
            return { spots: 4, colours: 2 };
        case 6:
            return { spots: 5, colours: 2 };
        case 7:
            return { spots: 5, colours: 3 };
        // Every spot on the wing, in three colours: position is no longer in question and the whole
        // trial is whether the colours came back in the right order.
        default:
            return { spots: 6, colours: 3 };
    }
}
const wingOf = (task) => ({
    cols: Number(task.params["wingCols"]),
    rows: Number(task.params["wingRows"]),
});
/** The reflection of a wing: every row read backwards. */
export function reflectWing(wing, cols) {
    return wing.map((_, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        return wing[row * cols + (cols - 1 - col)];
    });
}
/**
 * Whether reflecting this wing gives something different from copying it across unchanged.
 *
 * **The check the whole game rests on, and the error it is built to catch is the commonest one
 * children make.** Asked to mirror, a child very often TRANSLATES: they reproduce the pattern
 * shifted sideways rather than flipped, so the spot nearest the body comes out furthest from it.
 * On a wing whose rows read the same backwards, those two answers are identical — the trial cannot
 * tell a child who reflected from a child who copied, while looking exactly like one that could.
 *
 * With two columns a row reads the same backwards when its two spots match, so this rules out any
 * wing whose rows are all pairs of the same thing. Every other wing is a trial that means what it
 * says.
 */
export function reflectsToSomethingElse(wing, cols) {
    const flipped = reflectWing(wing, cols);
    return wing.some((value, i) => value !== flipped[i]);
}
export function generateMirror(task, formId, trialIndex, difficulty) {
    if (difficulty < MIRROR_MIN_DIFFICULTY || difficulty > MIRROR_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { cols, rows } = wingOf(task);
    const cells = cols * rows;
    const { spots, colours } = recipeFor(difficulty);
    if (spots > cells) {
        throw new Error(`difficulty ${difficulty} asks for ${spots} spots on a ${cells}-spot wing`);
    }
    if (colours > MIRROR_COLORS) {
        throw new Error(`difficulty ${difficulty} asks for ${colours} colours`);
    }
    /**
     * Draw until a wing turns up whose reflection is not also its copy.
     *
     * Bounded, and exhausting the bound throws rather than falling back to the last draw. A trial
     * where copying scores the same as reflecting must never reach a child: they would be right, and
     * they would have practised the thing this game exists to correct.
     */
    for (let attempt = 0; attempt < 200; attempt++) {
        const rand = mulberry32(seedFrom(`${formId}#${attempt}`, trialIndex, difficulty));
        const order = shuffle(Array.from({ length: cells }, (_, i) => i), rand);
        const wing = new Array(cells).fill(MIRROR_EMPTY);
        order.slice(0, spots).forEach((cell, i) => {
            // The first `colours` spots take one colour each, so a three-colour level never quietly
            // draws a two-colour wing.
            wing[cell] = i < colours ? i : Math.floor(rand() * colours);
        });
        if (reflectsToSomethingElse(wing, cols)) {
            return { cols, rows, colours, wing, answer: reflectWing(wing, cols) };
        }
    }
    throw new Error(`no reflectable wing for ${task.id} at difficulty ${difficulty} — every draw read the same ` +
        `backwards, so copying it across would have scored the same as reflecting it`);
}
/**
 * How many spots a finished wing has something on — which is NOT how many spots it has.
 *
 * **The rule for "done", kept where a test can see it.** A reflection has holes in it, so a
 * renderer that waited for every well on the wing to be filled would wait for an answer that is
 * wrong by construction, and a child who had finished correctly would sit in front of a completed
 * butterfly that never resolved. The tiling game shipped with exactly that mistake, in a renderer,
 * where nothing could catch it; this is the same rule for this game, moved somewhere it can be
 * asserted.
 */
export function spotsToPaint(stimulus) {
    return stimulus.answer.filter((value) => value !== MIRROR_EMPTY).length;
}
/** The painted wing has to match the reflection everywhere, empty spots included. */
export function scoreMirrorTrial(stimulus, painted) {
    return (painted.length === stimulus.answer.length &&
        stimulus.answer.every((value, i) => painted[i] === value));
}
/**
 * How many spots a child placed where a COPY would have put them rather than a reflection.
 *
 * The process signal this game has and the old one could not have had. A wrong wing is not very
 * informative on its own; a wrong wing that is exactly the given one slid across says precisely
 * what the child did, and it is the misconception a teacher can act on in the next lesson.
 *
 * Counted per spot rather than as a verdict on the whole wing, because a child half-way to the idea
 * mirrors some of it and slides the rest — which is what learning this looks like.
 */
export function translatedSpots(stimulus, painted) {
    let count = 0;
    for (let i = 0; i < stimulus.answer.length; i++) {
        const wanted = stimulus.answer[i];
        const copied = stimulus.wing[i];
        if (painted[i] === wanted)
            continue;
        if (copied !== MIRROR_EMPTY && painted[i] === copied)
            count++;
    }
    return count;
}
//# sourceMappingURL=mirror.js.map